import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getTabForPath, getPathForTab } from '@/config/routes';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Mail, Settings, Lock, Crown, User, CreditCard, Building2, UserCog } from 'lucide-react';
import { usePlan } from '@/context/PlanContext';
import { useOrganizations } from '@/context/OrganizationContext';
import artistIntroLogo from '../assets/artist_intro.svg';

interface User {
  _id: string;
  username: string;
  fullName?: string;
  user_type: string;
  email: string;
  domain: string;
  profilePicture?: string;
}

interface Domain {
  _id: string;
  slug: string;
  url: string;
  name?: string;
  isPublished?: boolean;
  hasCompletedOnboarding?: boolean;
  /** 'artist' (default) | 'management' — shown as a switcher badge. */
  siteType?: 'artist' | 'management';
}

interface TopBarProps {
  user: User | null;
  domains: Domain[];
  selectedDomain: string;
  setSelectedDomain: (domain: string) => void;
  siteName: string;
  handleLogout: () => void;
  setTab?: (tab: any) => void;
  currentTab?: string;
  unopenedLeadsCount?: number;
  isPreviewOpen?: boolean;
  onTogglePreview?: () => void;
  showPreviewToggle?: boolean;
  onHamburgerClick?: () => void;
  currentDomain?: Domain | null;
  /** Additional domain slugs the user can access via org memberships. */
  orgAccessibleSlugs?: string[];
  /** Site type per slug — badges management websites in the switcher. */
  siteTypeBySlug?: Record<string, 'artist' | 'management'>;
  /** True iff the user belongs to >= 1 organization. */
  hasOrganizations?: boolean;
  /** True iff the user is owner/admin in >= 1 management company. Scoped
   *  Members (= artist managers) should not see the management-company
   *  workspace; they go straight to the artist dashboard instead. */
  hasManagementRole?: boolean;
}

const TopBar: React.FC<TopBarProps> = ({
  user,
  domains,
  selectedDomain,
  setSelectedDomain,
  siteName,
  handleLogout,
  setTab: _setTabProp,
  currentTab: _currentTabProp,
  unopenedLeadsCount = 0,
  // These props are available but not currently used
  isPreviewOpen: _isPreviewOpen = false,
  onTogglePreview: _onTogglePreview,
  showPreviewToggle: _showPreviewToggle = false,
  onHamburgerClick,
  siteTypeBySlug = {},
  currentDomain: _currentDomain,
  orgAccessibleSlugs = [],
  hasOrganizations: _hasOrganizations = false,
  hasManagementRole = false,
}) => {
  const navigateRouter = useNavigate();
  const locationRouter = useLocation();
  const currentTab = getTabForPath(locationRouter.pathname);
  const setTab = (key: any) => navigateRouter(getPathForTab(key));
  const { hasContactLeadsAccess, openUpgradeModal } = usePlan();
  const { organizations } = useOrganizations();

  // Friendly role label for the profile dropdown. Backend stores everyone
  // who isn't super-admin as `domain-admin`, but the actual identity is:
  //   • owns a slug              → Artist
  //   • owner in some org        → Owner
  //   • admin in some org        → Company Admin
  //   • member in some org / has only artist-direct grants → Artist Representative
  const roleLabel: string = (() => {
    if (user?.user_type === 'super-admin') return 'Super Admin';
    if (user?.user_type === 'domain-admin') {
      if (user.domain) return 'Artist';
      const roles = organizations.map((o) => o.myRole);
      if (roles.includes('owner')) return 'Owner';
      if (roles.includes('admin')) return 'Company Admin';
      return 'Artist Representative';
    }
    return (user?.user_type || '').replace('-', ' ');
  })();
  const [searchQuery, setSearchQuery] = useState('');
  const [profileOpen, setProfileOpen] = useState(false);
  const [domainOpen, setDomainOpen] = useState(false);
  const domainDropdownRef = useRef<HTMLDivElement>(null);
  const profileDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (domainDropdownRef.current && !domainDropdownRef.current.contains(event.target as Node)) {
        setDomainOpen(false);
      }
      if (profileDropdownRef.current && !profileDropdownRef.current.contains(event.target as Node)) {
        setProfileOpen(false);
      }
    };

    if (domainOpen || profileOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [domainOpen, profileOpen]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    // TODO: Implement global search functionality
    console.log('Searching for:', searchQuery);
  };

  const handleDomainSelect = (domainSlug: string) => {
    setSelectedDomain(domainSlug);
    setDomainOpen(false);
  };

  const handleClearDomain = () => {
    setSelectedDomain('');
    setDomainOpen(false);
  };

  const handleLogoutClick = () => {
    handleLogout();
    setProfileOpen(false);
  };

  return (
    <div className="fixed top-0 left-0 lg:left-64 right-0 bg-card border-b border-border z-50">
      <div className="flex items-center justify-between relative h-[60px] lg:h-[70px] px-4 lg:px-8">
        {/* Left side - Hamburger + Brand (mobile) + Domain Selector */}
        <div className="flex items-center gap-2 lg:gap-4">
          {/* Hamburger - mobile only */}
          <button
            onClick={onHamburgerClick}
            className="lg:hidden p-2 -ml-1 rounded-lg hover:bg-secondary transition-colors"
            aria-label="Open menu"
          >
            <svg className="w-5 h-5 text-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          {/* Brand logo - centered on mobile (desktop shows in sidebar) */}
          <img src={artistIntroLogo} alt="Artist Intro" className="h-7 lg:hidden absolute left-1/2 -translate-x-1/2" />
          <div className="hidden lg:flex items-center gap-2">
            {user?.user_type === 'domain-admin' ? (
              // Domain-admin: render a dropdown only when the user has more
              // than one accessible slug across (owned domain ∪ org-mediated
              // slugs ∪ direct-grant slugs). With just one slug — including
              // the common direct-grant Manager case (no owned domain, one
              // assigned artist) — show a static badge instead.
              (() => {
                const allSlugs = new Set<string>();
                if (user?.domain) allSlugs.add(user.domain);
                for (const s of orgAccessibleSlugs) if (s) allSlugs.add(s);
                return allSlugs.size > 1;
              })() ? (
                <div className="relative" ref={domainDropdownRef}>
                  <button
                    onClick={() => setDomainOpen(!domainOpen)}
                    className="px-3 py-2 rounded-lg bg-secondary hover:bg-accent text-sm font-semibold text-foreground transition-colors flex items-center gap-2"
                  >
                    {siteName || selectedDomain || user?.domain || 'Domain'}
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      className={`transition-transform ${domainOpen ? 'rotate-180' : ''}`}
                    >
                      <polyline points="6 9 12 15 18 9"></polyline>
                    </svg>
                  </button>

                  {domainOpen && (
                    <div className="absolute top-full left-0 mt-1 bg-card border border-border rounded-lg shadow-lg z-50 min-w-[220px] max-h-64 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                      {/* Own domain first */}
                      {user?.domain && (
                        <button
                          onClick={() => handleDomainSelect(user.domain)}
                          className={`w-full text-left px-4 py-2 text-sm font-medium transition-colors ${
                            selectedDomain === user.domain || !selectedDomain
                              ? 'bg-primary/10 text-primary'
                              : 'text-foreground hover:bg-muted/50'
                          }`}
                        >
                          {user.domain}
                          <span className="text-[10px] uppercase tracking-wider text-muted-foreground ml-2">your domain</span>
                        </button>
                      )}
                      {orgAccessibleSlugs
                        .filter((slug) => slug && slug !== user?.domain)
                        .map((slug) => (
                          <button
                            key={slug}
                            onClick={() => handleDomainSelect(slug)}
                            className={`w-full text-left px-4 py-2 text-sm font-medium transition-colors ${
                              selectedDomain === slug
                                ? 'bg-primary/10 text-primary'
                                : 'text-foreground hover:bg-muted/50'
                            }`}
                          >
                            {slug}
                            <span className="text-[10px] uppercase tracking-wider text-muted-foreground ml-2">
                              {siteTypeBySlug?.[slug] === 'management' ? 'management website' : 'via org'}
                            </span>
                          </button>
                        ))}
                    </div>
                  )}
                </div>
              ) : (
                <span className="px-4 py-2.5 rounded-lg bg-secondary text-sm font-semibold text-foreground">
                  {selectedDomain || siteName || user?.domain || 'Domain'}
                </span>
              )
            ) : (
              <div className="relative" ref={domainDropdownRef}>
                <button
                  onClick={() => setDomainOpen(!domainOpen)}
                  className="px-3 py-2 rounded-lg bg-secondary hover:bg-accent text-sm font-medium text-foreground transition-colors flex items-center gap-2"
                >
                  {siteName || 'Select Domain'}
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    className={`transition-transform ${domainOpen ? 'rotate-180' : ''}`}
                  >
                    <polyline points="6 9 12 15 18 9"></polyline>
                  </svg>
                </button>
                
                {domainOpen && (
                  <div className="absolute top-full left-0 mt-1 bg-card border border-border rounded-lg shadow-lg z-50 min-w-[200px] max-h-48 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                    {domains.length === 0 ? (
                      <div className="p-3 text-sm text-muted-foreground whitespace-nowrap">
                        No domains available
                      </div>
                    ) : (
                      <>
                        <button
                          onClick={handleClearDomain}
                          className={`w-full text-left px-4 py-2 text-sm font-medium transition-colors ${
                            !selectedDomain
                              ? 'bg-primary/10 text-primary'
                              : 'text-foreground hover:bg-muted/50'
                          }`}
                        >
                          Select Domain
                        </button>
                        {domains.map(domain => (
                          <button
                            key={domain._id}
                            onClick={() => {
                              handleDomainSelect(domain.slug);
                            }}
                            className={`w-full text-left px-4 py-2 text-sm font-medium transition-colors ${
                              selectedDomain === domain.slug
                                ? 'bg-primary/10 text-primary'
                                : 'text-foreground hover:bg-muted/50'
                            }`}
                          >
                            {domain.slug}
                            {domain.siteType === 'management' && (
                              <span className="text-[10px] uppercase tracking-wider text-muted-foreground ml-2">management</span>
                            )}
                          </button>
                        ))}
                      </>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
          {selectedDomain && setTab && (
            <Button
              onClick={() => setTab('siteBranding')}
              variant="ghost"
              className={`hidden lg:flex ml-4 h-9 px-3 text-sm font-semibold rounded-lg items-center gap-2  ${
                currentTab === 'siteBranding'
                  ? 'bg-sidebar-accent text-primary hover:text-primary hover:bg-sidebar-accent'
                  : 'bg-secondary text-foreground hover:text-primary hover:bg-sidebar-hover'


              }`}
            >
              <Settings size={18} />
              Website Settings
            </Button>
          )}
        </div>

        {/* Center - Search (hidden on mobile) */}
        <div className="hidden lg:block flex-1 max-w-md mx-8">
          <form onSubmit={handleSearch} className="relative">
            <Input
              type="text"
              placeholder="Search system..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-border bg-secondary/50 focus:bg-card focus:border-primary text-sm placeholder:text-muted-foreground"
            />
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              className="absolute left-3.5 top-1/2 transform -translate-y-1/2 text-muted-foreground"
            >
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.35-4.35" />
            </svg>
          </form>
        </div>

        {/* Right side - Leads + Profile */}
        <div className="flex items-center gap-2 lg:gap-3">
          {selectedDomain && setTab && (
            <Button
              onClick={() => {
                if (!hasContactLeadsAccess) {
                  openUpgradeModal('contactLeadsAccess');
                } else {
                  setTab('contactWebsite');
                }
              }}
              variant="ghost"
              className={`hidden lg:flex items-center gap-2 relative h-9 px-3 rounded-lg text-sm font-semibold ${
                !hasContactLeadsAccess
                  ? 'bg-primary-light text-primary hover:bg-primary-light hover:text-primary'
                  : currentTab === 'contactWebsite'
                    ? 'bg-sidebar-accent text-primary hover:text-primary hover:bg-sidebar-accent'
                    : 'bg-secondary text-foreground hover:text-primary hover:bg-sidebar-hover'
              }`}
              title={!hasContactLeadsAccess ? 'Upgrade to unlock Website Leads' : 'View Website Leads'}
            >
              {!hasContactLeadsAccess ? (
                <Lock size={18} />
              ) : (
                <Mail size={18} />
              )}
              Website Leads
              {!hasContactLeadsAccess ? (
                <Crown size={14} className="text-primary" />
              ) : unopenedLeadsCount > 0 ? (
                <span className="absolute -top-1.5 -right-1.5 bg-danger text-white text-xs font-bold rounded-full min-w-[20px] h-5 flex items-center justify-center px-1.5">
                  {unopenedLeadsCount > 99 ? '99+' : unopenedLeadsCount}
                </span>
              ) : null}
            </Button>
          )}
          <div className="relative" ref={profileDropdownRef}>
            <button
              onClick={() => setProfileOpen(!profileOpen)}
              className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-secondary transition-colors"
            >
              <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center text-primary text-sm font-semibold overflow-hidden">
                {user?.profilePicture ? (
                  <img src={user.profilePicture} alt="" className="w-full h-full object-cover" />
                ) : (
                  (user?.fullName || user?.username)?.charAt(0).toUpperCase() || 'U'
                )}
              </div>
              <div className="text-left hidden sm:block">
                <div className="text-sm font-semibold text-foreground">{user?.fullName || user?.username}</div>
                <div className="text-xs text-muted-foreground">
                  {roleLabel}
                </div>
              </div>
            </button>

            {profileOpen && (
              <div className="absolute top-full right-0 mt-1 w-[calc(100vw-2rem)] sm:w-80 bg-card border border-border rounded-lg shadow-lg z-50 overflow-hidden">
                {/* Profile Header */}
                <div className="p-4 border-b border-border bg-secondary/30">
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-lg bg-primary/10 flex items-center justify-center text-primary text-base font-semibold overflow-hidden">
                      {user?.profilePicture ? (
                        <img src={user.profilePicture} alt="" className="w-full h-full object-cover" />
                      ) : (
                        (user?.fullName || user?.username)?.charAt(0).toUpperCase() || 'U'
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-foreground">{user?.fullName || user?.username}</div>
                      <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
                    </div>
                  </div>
                </div>

                {/* Profile Info */}
                <div className="p-4 space-y-3 border-b border-border">
                  <div>
                    <div className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Role</div>
                    <div className="text-sm text-foreground font-medium mt-0.5">
                      {roleLabel}
                    </div>
                  </div>
                  {user?.user_type === 'domain-admin' && (
                    <div>
                      <div className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Domain</div>
                      <div className="text-sm text-foreground font-medium mt-0.5">{user?.domain}</div>
                    </div>
                  )}
                </div>

                {/* Navigation Links */}
                <div className="p-2 border-b border-border">
                  <button
                    onClick={() => { navigateRouter('/profile'); setProfileOpen(false); }}
                    className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-foreground hover:bg-muted/50 transition-colors"
                  >
                    <User size={16} className="text-muted-foreground" />
                    Profile
                  </button>
                  {/* Billing is suppressed on management websites — their
                      entitlements are admin-granted (a separate payment
                      model is future work). */}
                  {siteTypeBySlug[selectedDomain] !== 'management' && (
                    <button
                      onClick={() => { navigateRouter('/billing'); setProfileOpen(false); }}
                      className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-foreground hover:bg-muted/50 transition-colors"
                    >
                      <CreditCard size={16} className="text-muted-foreground" />
                      Billing
                    </button>
                  )}
                  {user?.user_type === 'domain-admin' && user?.domain && (
                    <button
                      onClick={() => { navigateRouter('/manager-access'); setProfileOpen(false); }}
                      className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-foreground hover:bg-muted/50 transition-colors"
                    >
                      <UserCog size={16} className="text-muted-foreground" />
                      Manager Access
                    </button>
                  )}
                  {user?.user_type === 'super-admin' && selectedDomain && (
                    <button
                      onClick={() => { navigateRouter('/manager-access'); setProfileOpen(false); }}
                      className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-foreground hover:bg-muted/50 transition-colors"
                    >
                      <UserCog size={16} className="text-muted-foreground" />
                      Manager Access
                      <span className="ml-auto text-xs text-muted-foreground font-mono truncate max-w-[100px]">{selectedDomain}</span>
                    </button>
                  )}
                  {hasManagementRole && user?.user_type !== 'super-admin' && (
                    <button
                      onClick={() => { navigateRouter('/organizations'); setProfileOpen(false); }}
                      className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-foreground hover:bg-muted/50 transition-colors"
                    >
                      <Building2 size={16} className="text-muted-foreground" />
                      Management Companies
                    </button>
                  )}
                </div>

                {/* Logout Button */}
                <div className="p-3">
                  <Button
                    onClick={handleLogoutClick}
                    variant="destructive"
                    className="w-full h-10 rounded-lg font-medium"
                  >
                    Logout
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TopBar;
